import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, X, Tag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { callOutcomes, cn, apiRequest, formatDateTime } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const customerTagStyles = {
  easy: "bg-green-100 text-green-800",
  normal: "bg-blue-100 text-blue-800",
  difficult: "bg-red-100 text-red-800"
};

export const CallLogModal = ({
  open,
  onClose,
  contact, // { id, name, phone, status, type: 'lead' | 'customer', branch_id, tag }
  onSuccess
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [remark, setRemark] = useState("");
  const [followUpDate, setFollowUpDate] = useState(null);
  const [bookingDate, setBookingDate] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!outcome) {
      toast.error("Please select call outcome");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        [contact.type === "lead" ? "lead_id" : "customer_id"]: contact.id,
        cre_id: user.id,
        branch_id: contact.branch_id || user.branch_id,
        outcome,
        remark: remark || null,
        follow_up_date: followUpDate ? followUpDate.toISOString() : null,
        booking_date: bookingDate ? bookingDate.toISOString() : null
      };

      await apiRequest("/call-logs", {
        method: "POST",
        body: JSON.stringify(payload)
      });

      toast.success("Call logged successfully");
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(error.message || "Failed to log call");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOutcome("");
    setRemark("");
    setFollowUpDate(null);
    setBookingDate(null);
    onClose();
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="call-log-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Log Call</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Auto-filled fields (read-only) */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <p className="font-medium" data-testid="call-log-name">{contact.name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <p className="font-mono" data-testid="call-log-phone">{contact.phone}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <p className="capitalize" data-testid="call-log-status">{contact.status?.replace("_", " ")}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Date/Time</Label>
              <p className="font-mono text-sm" data-testid="call-log-datetime">
                {formatDateTime(new Date().toISOString())}
              </p>
            </div>
            {contact.type === "customer" && contact.tag && (
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground">Customer Tag</Label>
                <div className="mt-1">
                  <span 
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${customerTagStyles[contact.tag] || customerTagStyles.normal}`}
                    data-testid="call-log-customer-tag"
                  >
                    <Tag className="w-3 h-3" />
                    {contact.tag.charAt(0).toUpperCase() + contact.tag.slice(1)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* CRE input fields */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="outcome">Call Outcome *</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger data-testid="call-outcome-select">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  {callOutcomes.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="remark">Remark</Label>
              <Textarea
                id="remark"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add notes about the call..."
                className="resize-none"
                rows={3}
                data-testid="call-remark-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Follow-up Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-normal",
                        !followUpDate && "text-muted-foreground"
                      )}
                      data-testid="follow-up-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                      {followUpDate ? format(followUpDate, "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={followUpDate}
                      onSelect={setFollowUpDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>Booking Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-normal",
                        !bookingDate && "text-muted-foreground"
                      )}
                      data-testid="booking-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                      {bookingDate ? format(bookingDate, "dd MMM yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={bookingDate}
                      onSelect={setBookingDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="h-11" data-testid="call-log-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="h-11" data-testid="call-log-submit">
              {loading ? "Saving..." : "Save Call Log"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CallLogModal;
